/**
 * ⓘ BİLGİ İKONU (kullanıcı kararı 19.10.2026).
 *
 * Anormallik DEĞİLDİR: yalnızca açıklama balonunun yerini gösterir. Uyarı
 * işaretleri (üçgen / ünlem, `UyariIkonu`) anormallik demek olmaya devam
 * eder; ⓘ hiçbir zaman sarı, kırmızı ya da yeşil olmaz — soluk metin rengi.
 *
 * Balonun metni TEK sözlükten (`M.formul`, anahtarlar sözleşmede); seçime göre
 * değişen durumda (sanal hesapta temettü yok sayıldıysa) o seçimin metni.
 */

import type { DegerFormulu, DegerFormuluKodu } from "@parafon/contracts/local-api";
import type { ReactNode } from "react";
import { Aciklama } from "./Aciklama";
import { M } from "../dil";
import { cx } from "./primitives";
import { UyariIkonu } from "./UyariIkonu";

export function formulMetni(kod: DegerFormuluKodu, temettuHaric = false): string {
  const f: DegerFormulu = M.formul[kod];
  return temettuHaric && f.temettuHaric !== undefined ? f.temettuHaric : f.formul;
}

const BilgiSimgesi = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" className="block">
    <circle cx="6" cy="6" r="5.25" fill="none" stroke="currentColor" strokeWidth="1.1" />
    <rect x="5.4" y="5" width="1.2" height="3.8" rx="0.6" fill="currentColor" />
    <circle cx="6" cy="3.4" r="0.75" fill="currentColor" />
  </svg>
);

/**
 * BAŞLIĞIN TAMAMI DOKUNMA ALANI (kullanıcı kararı 20.10.2026): ⓘ telefonda
 * parmakla tutturulamayacak kadar küçüktü. Başlık yazısı + işaret tek düğme;
 * ⓘ yalnızca "burada açıklama var" işareti.
 *
 * UYARI VARSA: ⓘ'nin yerinde uyarı işareti (sarı üçgen) — başlıkta tek işaret,
 * tek balon. Balonda üstte formül, bir boşluk, altta uyarının metni.
 *
 * Ekran okuyucu: düğmenin adı başlığın adı + "nasıl hesaplanır?" (uyarı
 * varsa "uyarı var" da).
 */
export function BilgiBasligi({
  children,
  ad,
  icerik,
  uyari,
  className,
}: {
  /** Görünen başlık (alt etiketiyle birlikte olabilir). */
  readonly children: ReactNode;
  /** Balonun ilk satırı ve ekran okuyucu adı. */
  readonly ad: string;
  /** Açıklama (formül). */
  readonly icerik: ReactNode;
  /** Varsa: ⓘ yerine uyarı işareti; balonda formülün altında. */
  readonly uyari?: ReactNode;
  readonly className?: string | undefined;
}) {
  const uyariVar = uyari !== undefined && uyari !== null && uyari !== false;
  return (
    <Aciklama
      className={cx("pf-bilgi-baslik", className)}
      label={uyariVar ? M.rapor.bilgi.uyariVar(ad) : M.rapor.bilgi.nasil(ad)}
      icerik={
        <>
          <span className="pf-aciklama-satir font-semibold text-ink">{ad}</span>
          <span className="pf-aciklama-satir">{icerik}</span>
          {uyariVar && <span className="pf-aciklama-satir pf-aciklama-ayrac text-warn">{uyari}</span>}
        </>
      }
    >
      {children}
      <span className={uyariVar ? "pf-bilgi pf-bilgi-uyari text-warn" : "pf-bilgi"}>
        {uyariVar ? <UyariIkonu tone="warn" /> : <BilgiSimgesi />}
      </span>
    </Aciklama>
  );
}

/** Sözlükteki formülle başlık. */
export function FormulBasligi({
  kod,
  temettuHaric = false,
  uyari,
  className,
  children,
}: {
  readonly kod: DegerFormuluKodu;
  readonly temettuHaric?: boolean;
  readonly uyari?: ReactNode;
  readonly className?: string | undefined;
  readonly children: ReactNode;
}) {
  const f: DegerFormulu = M.formul[kod];
  return (
    <BilgiBasligi ad={f.ad} icerik={formulMetni(kod, temettuHaric)} uyari={uyari} className={className}>
      {children}
    </BilgiBasligi>
  );
}

export function BilgiIkonu({
  kod,
  temettuHaric = false,
}: {
  readonly kod: DegerFormuluKodu;
  readonly temettuHaric?: boolean;
}) {
  const f: DegerFormulu = M.formul[kod];
  return <BilgiDugmesi ad={f.ad} icerik={formulMetni(kod, temettuHaric)} />;
}

/** Yalnız ⓘ (başlığa bağlanamayan yerler için). */
export function BilgiDugmesi({ ad, icerik }: { readonly ad: string; readonly icerik: ReactNode }) {
  return (
    <Aciklama
      className="pf-bilgi"
      label={M.rapor.bilgi.nasil(ad)}
      icerik={
        <>
          <span className="pf-aciklama-satir font-semibold text-ink">{ad}</span>
          <span className="pf-aciklama-satir">{icerik}</span>
        </>
      }
    >
      <BilgiSimgesi />
    </Aciklama>
  );
}
